import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { profileSource } from "@/lib/accountProfile";

/**
 * DemographicsTab — edit per-instrument demographics_config array.
 * Each item: { key, type, required, order, label_id, label_en, description_id, description_en, placeholder_id, placeholder_en, options?, profile_key? }
 * profile_key picks the account profile value that pre-fills the field ("" = by key, "none" = never).
 */
const PROFILE_KEYS = [
  { value: "name", label_id: "Nama", label_en: "Name" },
  { value: "age", label_id: "Usia (dari tanggal lahir)", label_en: "Age (from birth date)" },
  { value: "gender", label_id: "Jenis kelamin", label_en: "Gender" },
  { value: "education", label_id: "Pendidikan", label_en: "Education" },
  { value: "occupation", label_id: "Pekerjaan", label_en: "Occupation" },
  { value: "marital_status", label_id: "Status pernikahan", label_en: "Marital status" },
];
const TYPES = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "select", label: "Select" },
];

export default function DemographicsTab({ inst, set, lang }) {
  const fields = (inst.demographics_config || []).slice();
  const setFields = (arr) => set("demographics_config", arr);
  const upd = (i, patch) => setFields(fields.map((f, k) => (k === i ? { ...f, ...patch } : f)));
  const rm = (i) => setFields(fields.filter((_, k) => k !== i).map((f, k) => ({ ...f, order: k + 1 })));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const arr = [...fields];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setFields(arr.map((f, k) => ({ ...f, order: k + 1 })));
  };
  const add = () =>
    setFields([
      ...fields,
      { key: `field_${Date.now().toString(36)}`, type: "text", required: false, order: fields.length + 1, label_id: "", label_en: "", description_id: "", description_en: "" },
    ]);
  const addOption = (i) => upd(i, { options: [...(fields[i].options || []), { value: `opt_${(fields[i].options || []).length + 1}`, label_id: "", label_en: "" }] });
  const updOption = (i, oi, patch) => {
    const opts = (fields[i].options || []).map((o, k) => (k === oi ? { ...o, ...patch } : o));
    upd(i, { options: opts });
  };
  const rmOption = (i, oi) => upd(i, { options: (fields[i].options || []).filter((_, k) => k !== oi) });

  return (
    <div className="space-y-4" data-wrap="responsive">
      <div className="p-4 rounded-2xl bg-cream-100/60 border border-border text-sm text-muted-foreground">
        {lang === "id"
          ? "Setiap field bersifat opsional atau wajib diisi. Field wajib harus dijawab responden sebelum mulai mengerjakan instrumen. Bila responden masuk dengan akun yang punya data profil (nama, tanggal lahir, jenis kelamin, pendidikan, pekerjaan, status pernikahan) dan mengonfirmasi bahwa ia sendiri yang mengisi, field yang cocok terisi otomatis. Deskripsi akan ditampilkan di bawah label (mis: “Ini akan menentukan avatar Anda di layar hasil.”). Jika field diisi responden dan instrumen ini berbayar, data akan dipakai untuk mempersonalisasi laporan lengkap (nama, avatar, dsb.). Di halaman hasil ringkas gratis, data demografi tidak ditampilkan."
          : "Each field can be optional or required. Required fields must be answered before the respondent starts the instrument. When a signed-in respondent has profile data (name, birth date, gender, education, occupation, marital status) and confirms they are the one answering, matching fields are pre-filled. Descriptions appear under the label (e.g. “This will determine your avatar on the result screen.”). If filled and the instrument is paid, the info personalises the full report (name, avatar, etc.). On the free summary result page, demographic data is not shown."}
      </div>

      {fields.map((f, i) => (
        <div key={f.key || i} className="p-5 rounded-2xl border border-border bg-card space-y-3" data-testid={`demo-cfg-row-${i}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground w-16">Key</Label>
            <Input value={f.key || ""} onChange={(e) => upd(i, { key: e.target.value })} className="h-8 max-w-xs font-mono text-xs" data-testid={`demo-cfg-key-${i}`} />
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">Type</Label>
            <select value={f.type || "text"} onChange={(e) => upd(i, { type: e.target.value })} className="h-8 rounded-md border border-border bg-background text-xs px-2" data-testid={`demo-cfg-type-${i}`}>
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <label className="flex items-center gap-2 text-xs ml-2">
              <Switch checked={!!f.required} onCheckedChange={(v) => upd(i, { required: v })} data-testid={`demo-cfg-req-${i}`} />
              {lang === "id" ? "Wajib diisi" : "Required"}
            </label>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">{lang === "id" ? "Isi dari akun" : "Fill from account"}</Label>
            <select
              value={f.profile_key || ""}
              onChange={(e) => upd(i, { profile_key: e.target.value })}
              className="h-8 rounded-md border border-border bg-background text-xs px-2"
              data-testid={`demo-cfg-profile-${i}`}
            >
              <option value="">
                {(() => {
                  const auto = PROFILE_KEYS.find((p) => p.value === profileSource({ key: f.key }));
                  const name = auto ? (auto[`label_${lang}`] || auto.label_id) : (lang === "id" ? "tidak ada" : "none");
                  return `${lang === "id" ? "Otomatis" : "Automatic"} (${name})`;
                })()}
              </option>
              {PROFILE_KEYS.map((p) => <option key={p.value} value={p.value}>{p[`label_${lang}`] || p.label_id}</option>)}
              <option value="none">{lang === "id" ? "Jangan isi dari akun" : "Never fill from account"}</option>
            </select>
            <div className="ml-auto flex items-center gap-1">
              <Button size="icon" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`demo-cfg-up-${i}`}><ArrowUp className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => move(i, 1)} disabled={i === fields.length - 1} data-testid={`demo-cfg-down-${i}`}><ArrowDown className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => rm(i)} className="text-destructive" data-testid={`demo-cfg-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Label (ID)" : "Label (ID)"}</Label>
              <Input value={f.label_id || ""} onChange={(e) => upd(i, { label_id: e.target.value })} placeholder={lang === "id" ? "cth: Gender Kamu" : "e.g. Your gender"} data-testid={`demo-cfg-label-id-${i}`} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Label (EN)" : "Label (EN)"}</Label>
              <Input value={f.label_en || ""} onChange={(e) => upd(i, { label_en: e.target.value })} placeholder="e.g. Your gender" data-testid={`demo-cfg-label-en-${i}`} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Deskripsi (ID)" : "Description (ID)"}</Label>
              <Textarea rows={2} value={f.description_id || ""} onChange={(e) => upd(i, { description_id: e.target.value })} placeholder={lang === "id" ? "cth: Opsional. Ini akan menentukan avatar Anda di layar hasil." : "e.g. Optional. This will determine your avatar on the result screen."} data-testid={`demo-cfg-desc-id-${i}`} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Deskripsi (EN)" : "Description (EN)"}</Label>
              <Textarea rows={2} value={f.description_en || ""} onChange={(e) => upd(i, { description_en: e.target.value })} placeholder="e.g. Optional. This will determine your avatar." data-testid={`demo-cfg-desc-en-${i}`} />
            </div>
            {f.type !== "select" && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Placeholder (ID)" : "Placeholder (ID)"}</Label>
                  <Input value={f.placeholder_id || ""} onChange={(e) => upd(i, { placeholder_id: e.target.value })} data-testid={`demo-cfg-ph-id-${i}`} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Placeholder (EN)" : "Placeholder (EN)"}</Label>
                  <Input value={f.placeholder_en || ""} onChange={(e) => upd(i, { placeholder_en: e.target.value })} data-testid={`demo-cfg-ph-en-${i}`} />
                </div>
              </>
            )}
          </div>

          {f.type === "select" && (
            <div className="p-3 rounded-xl bg-secondary/40 border border-border space-y-2" data-testid={`demo-cfg-options-${i}`}>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{lang === "id" ? "Opsi" : "Options"}</div>
              {(f.options || []).map((o, oi) => (
                <div key={oi} className="grid md:grid-cols-[120px_1fr_1fr_auto] gap-2 items-end">
                  <Input value={o.value || ""} onChange={(e) => updOption(i, oi, { value: e.target.value })} placeholder="value" className="h-9 font-mono text-xs" data-testid={`demo-cfg-opt-val-${i}-${oi}`} />
                  <Input value={o.label_id || ""} onChange={(e) => updOption(i, oi, { label_id: e.target.value })} placeholder="Label ID" className="h-9" data-testid={`demo-cfg-opt-id-${i}-${oi}`} />
                  <Input value={o.label_en || ""} onChange={(e) => updOption(i, oi, { label_en: e.target.value })} placeholder="Label EN" className="h-9" data-testid={`demo-cfg-opt-en-${i}-${oi}`} />
                  <Button size="icon" variant="ghost" onClick={() => rmOption(i, oi)} className="text-destructive" data-testid={`demo-cfg-opt-rm-${i}-${oi}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addOption(i)} className="rounded-full" data-testid={`demo-cfg-opt-add-${i}`}><Plus className="w-3 h-3 mr-1" /> {lang === "id" ? "Tambah Opsi" : "Add Option"}</Button>
            </div>
          )}
        </div>
      ))}

      <Button variant="outline" onClick={add} className="rounded-full" data-testid="demo-cfg-add"><Plus className="w-4 h-4 mr-1" /> {lang === "id" ? "Tambah Field" : "Add Field"}</Button>
    </div>
  );
}
